import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search, Video, X } from 'lucide-react';
import { ablyConnectionManager } from '../../utils/miscellaneous/ablyConnectionManager';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import {
  cancelPharmacistDriverVideoCall,
  endPharmacistDriverVideoCall,
  endPharmacistDriverVideoCallOnUnload,
  fetchPharmacistDriverCallDrivers,
  initiatePharmacistDriverVideoCall,
  notifyPharmacistDriverVideoCall,
} from './pharmacistDriverVideoCallApi';
import PharmacistDriverVideoRoom from './PharmacistDriverVideoRoom';

const RING_TIMEOUT_MS = 30000;

const getValidPhoto = (photo) => {
  const value = String(photo || '').trim();
  return value && !['null', 'undefined'].includes(value.toLowerCase()) ? value : '';
};

const getInitials = (value) => {
  const name = String(value || '').trim();
  if (!name) return 'U';

  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const DriverAvatar = ({ driver }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const validPhoto = getValidPhoto(driver?.photo);
  const shouldShowImage = validPhoto && !imageFailed;

  return (
    <div className="support-chat-list-avatar">
      {shouldShowImage ? (
        <img
          src={validPhoto}
          onError={() => setImageFailed(true)}
          alt=""
          className="object-cover"
        />
      ) : (
        <span className="support-chat-list-avatar-fallback">
          {getInitials(driver?.name)}
        </span>
      )}
      <span className="support-chat-list-avatar-dot online"></span>
    </div>
  );
};

const normalizeCall = (data) => {
  if (!data) return null;
  const driverPhoto = data.driver?.driver_photo ?? data.driver?.photo ?? data.driver_photo ?? data.driverPhoto ?? data.photo;
  const hasValue = (value) => value !== undefined && value !== null && value !== '';

  return {
    callId: data.call_id ?? data.callId,
    pharmacyId: data.pharmacy_id ?? data.pharmacyId,
    driverId: data.driver?.id ?? data.driver_id ?? data.driverId,
    driverName: data.driver?.name ?? data.driver_name ?? data.driverName,
    ...(driverPhoto ? { driverPhoto } : {}),
    pharmacistId: data.pharmacist?.id ?? data.pharmacist_id ?? data.pharmacistId,
    pharmacistName: data.pharmacist?.name ?? data.pharmacist_name ?? data.pharmacistName,
    status: data.status,
    ...(hasValue(data.vonage_session_id) ? { vonage_session_id: data.vonage_session_id } : {}),
    ...(hasValue(data.vonage_token) ? { vonage_token: data.vonage_token } : {}),
    ...(hasValue(data.application_id) ? { application_id: data.application_id } : {}),
    // Which SDK to use, and where to connect. The session/token slots above are
    // reused for MediTalk (LiveKit room ref + JWT); application_id is empty for
    // MediTalk on purpose, so it is dropped by hasValue and the room falls back
    // to video_provider instead.
    ...(hasValue(data.video_provider) ? { video_provider: data.video_provider } : {}),
    ...(hasValue(data.livekit_url) ? { livekit_url: data.livekit_url } : {}),
    ...(data.push_delivery ? { pushDelivery: data.push_delivery } : {}),
    flow: data.flow,
  };
};

const describePushDeliveryProblem = (pushDelivery) => {
  if (!pushDelivery) return '';
  if (Number(pushDelivery.token_rows_found || 0) <= 0) {
    return 'Assistant app is online, but this phone is not registered for incoming calls yet.';
  }
  if (Number(pushDelivery.fcm_tokens_targeted || 0) <= 0 && Number(pushDelivery.voip_attempted || 0) <= 0) {
    return 'Assistant app token was found, but no push target was available.';
  }
  if (Number(pushDelivery.tokens_sent || 0) <= 0 && Number(pushDelivery.voip_sent || 0) <= 0) {
    return pushDelivery.failure_reason || pushDelivery.voip_failure_reason || 'Backend could not send the incoming call push.';
  }
  return '';
};

const extractDrivers = (payload) => {
  const toList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (value?.driverUser || value?.driver_id || value?.delivery_driver_id || value?.driver?.id) {
      return [value];
    }
    if (value?.data) return toList(value.data);
    if (value?.drivers) return toList(value.drivers);
    if (typeof value === 'object') {
      return Object.values(value).filter((item) => item && typeof item === 'object');
    }
    return [];
  };

  const list = toList(payload);

  const seen = new Set();
  return list
    .map((item) => {
      const assistant = item?.driverUser || item?.driver || item;
      const id = assistant?.id ?? item?.driver_id ?? item?.delivery_driver_id ?? item?.driverId;
      return {
        id,
        name: assistant?.name || item?.driver_name || item?.name || `Assistant ${id ?? ''}`.trim(),
        email: assistant?.email || item?.driver_email || '',
        photo: getValidPhoto(assistant?.photo)
          || getValidPhoto(assistant?.user_photo)
          || getValidPhoto(item?.driver_photo)
          || getValidPhoto(item?.photo)
          || getValidPhoto(item?.avatar),
        onlineStatus: item?.online_status || assistant?.online_status || '',
      };
    })
    .filter((driver) => {
      if (driver.id == null || seen.has(String(driver.id))) return false;
      if (String(driver.onlineStatus || '').toLowerCase() !== 'online') return false;
      seen.add(String(driver.id));
      return true;
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const PharmacistDriverVideoCallManager = ({ triggerPlacement = 'floating', refreshKey = 0 }) => {
  const { pharmacyId, loginUserID, authData, profileData, loginType } = useCustomSelectors();
  const [headerTriggerContainer, setHeaderTriggerContainer] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState('idle');
  const timeoutRef = useRef(null);
  const activeCallRef = useRef(null);
  const callStateRef = useRef('idle');
  const unloadCleanupSentRef = useRef(false);
  const notifiedCallIdsRef = useRef(new Set());

  const pharmacistId = useMemo(() => loginUserID ?? profileData?.user_id ?? authData?.data?.pharmacyloginuser?.[0]?.id ?? null, [authData, loginUserID, profileData?.user_id]);
  const isLoggedInPharmacist = useMemo(() => {
    const userType = profileData?.type ?? loginType ?? authData?.data?.pharmacyloginuser?.[0]?.type;
    return String(userType || '').trim().toLowerCase() === 'pharmacist';
  }, [authData, loginType, profileData?.type]);
  const canShowCallOption = Boolean(pharmacyId && pharmacistId && isLoggedInPharmacist);

  useEffect(() => {
    if (triggerPlacement !== 'header') return undefined;

    const syncHeaderTriggerContainer = () => {
      setHeaderTriggerContainer(document.getElementById('pharmacist-driver-call-header-action'));
    };

    syncHeaderTriggerContainer();
    const observer = new MutationObserver(syncHeaderTriggerContainer);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [triggerPlacement]);

  useEffect(() => {
    activeCallRef.current = activeCall;
    unloadCleanupSentRef.current = false;
  }, [activeCall]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.dispatchEvent(new CustomEvent('pharmacist-driver-video-call-active', {
      detail: { active: !!activeCall },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('pharmacist-driver-video-call-active', {
        detail: { active: false },
      }));
    };
  }, [activeCall]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    const endActiveCallBeforePageLeaves = () => {
      const current = activeCallRef.current;
      if (!current?.callId || unloadCleanupSentRef.current) return;
      if (!['requesting', 'accepted'].includes(callStateRef.current)) return;

      unloadCleanupSentRef.current = true;
      endPharmacistDriverVideoCallOnUnload(current.callId);
    };

    window.addEventListener('pagehide', endActiveCallBeforePageLeaves);
    window.addEventListener('beforeunload', endActiveCallBeforePageLeaves);

    return () => {
      window.removeEventListener('pagehide', endActiveCallBeforePageLeaves);
      window.removeEventListener('beforeunload', endActiveCallBeforePageLeaves);
    };
  }, []);

  useEffect(() => {
    if (!pharmacyId || !pharmacistId) return undefined;

    const eventName = `pharmacy-${pharmacyId}`;
    const subscriptionId = `pharmacist-driver-video-call-${eventName}`;
    const callback = (message) => {
      const payload = message?.data;
      if (payload?.type !== 'pharmacist_driver_video_call_status') return;
      const normalized = normalizeCall(payload);
      const current = activeCallRef.current;
      if (!current?.callId || normalized?.callId !== current.callId) return;
      if (normalized.pharmacistId != null && String(normalized.pharmacistId) !== String(pharmacistId)) return;

      // Identity-stable merge. A status message that changes nothing must return
      // the SAME object: the video room's connect effect keys off this value, and
      // handing it a fresh-but-equal object made it tear the LiveKit room down and
      // rebuild it mid-call (most visibly on `accepted`, i.e. the exact moment the
      // assistant answered).
      setActiveCall((previous) => {
        if (previous?.callId !== normalized.callId) return previous;

        const merged = {
          ...previous,
          ...normalized,
          driverPhoto: normalized.driverPhoto || previous.driverPhoto,
          // The pharmacy-wide realtime payload no longer carries participant
          // names -- it reaches every assistant device in the pharmacy, so the
          // names were readable far more widely than the one call they belong
          // to. normalizeCall still sets these keys, just as undefined, and an
          // unguarded spread would blank them on the first status message and
          // leave the call header reading "Assistant". The caller already holds
          // both names from the initiate response.
          driverName: normalized.driverName || previous.driverName,
          pharmacistName: normalized.pharmacistName || previous.pharmacistName,
          vonage_session_id: normalized.vonage_session_id || previous.vonage_session_id,
          vonage_token: normalized.vonage_token || previous.vonage_token,
          application_id: normalized.application_id || previous.application_id,
          video_provider: normalized.video_provider || previous.video_provider,
          livekit_url: normalized.livekit_url || previous.livekit_url,
        };

        const keys = new Set([...Object.keys(previous), ...Object.keys(merged)]);
        for (const key of keys) {
          if (previous[key] !== merged[key]) return merged;
        }

        return previous;
      });

      if (normalized.status === 'accepted') {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setCallState('accepted');
      } else if (['declined', 'cancelled', 'expired', 'ended'].includes(normalized.status)) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setActiveCall(null);
        setCallState('idle');
      }
    };

    ablyConnectionManager.subscribe('pharmacy-notifications', eventName, callback, subscriptionId);
    return () => {
      ablyConnectionManager.unsubscribe(subscriptionId);
    };
  }, [pharmacistId, pharmacyId]);

  const loadDrivers = async () => {
    setLoadingDrivers(true);
    setNotice(null);
    try {
      const response = await fetchPharmacistDriverCallDrivers();
      setDrivers(extractDrivers(response));
    } catch (error) {
      setNotice('Unable to load assistants right now.');
    } finally {
      setLoadingDrivers(false);
    }
  };

  useEffect(() => {
    if (triggerPlacement !== 'chat-tab' || !canShowCallOption) return;
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerPlacement, canShowCallOption, refreshKey]);

  const openPicker = () => {
    if (activeCallRef.current) return;
    setIsPickerOpen(true);
    if (drivers.length === 0) {
      loadDrivers();
    }
  };

  const startCall = async (assistantId = selectedDriverId) => {
    if (!assistantId || starting) return;
    setSelectedDriverId(assistantId);
    setStarting(true);
    setNotice(null);
    try {
      const selectedDriver = drivers.find((driver) => String(driver.id) === String(assistantId));
      const response = normalizeCall(await initiatePharmacistDriverVideoCall(assistantId, {
        deferNotify: true,
      }));
      const pushProblem = describePushDeliveryProblem(response?.pushDelivery);
      if (process.env.NODE_ENV === 'development' && response?.pushDelivery) {
        // eslint-disable-next-line no-console
        console.info('[pharmacist-driver-call] push_delivery', response.pushDelivery);
      }
      if (pushProblem) {
        setNotice(pushProblem);
      }
      setActiveCall({
        ...response,
        driverName: response?.driverName || selectedDriver?.name,
        driverPhoto: response?.driverPhoto || selectedDriver?.photo,
      });
      setCallState('requesting');
      setIsPickerOpen(false);

      timeoutRef.current = window.setTimeout(() => {
        const current = activeCallRef.current;
        if (!current?.callId || callStateRef.current === 'accepted') return;
        cancelPharmacistDriverVideoCall(current.callId, 'timeout').catch(() => {});
        setCallState('expired');
        window.setTimeout(() => {
          setActiveCall(null);
          setCallState('idle');
        }, 2500);
      }, RING_TIMEOUT_MS);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to start the video call.';
      setNotice(message);
    } finally {
      setStarting(false);
    }
  };

  const closeCall = async () => {
    const current = activeCallRef.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (current?.callId) {
      if (callState === 'requesting') {
        cancelPharmacistDriverVideoCall(current.callId, 'pharmacist_cancelled').catch(() => {});
      } else if (callState === 'accepted') {
        endPharmacistDriverVideoCall(current.callId).catch(() => {});
      }
    }

    setActiveCall(null);
    setCallState('idle');
  };

  const notifyDriverWhenCallerReady = useCallback(async (callId) => {
    if (!callId || notifiedCallIdsRef.current.has(String(callId))) return;
    notifiedCallIdsRef.current.add(String(callId));
    try {
      await notifyPharmacistDriverVideoCall(callId);
    } catch (error) {
      notifiedCallIdsRef.current.delete(String(callId));
    }
  }, []);

  const filteredDrivers = drivers.filter((driver) => {
    const needle = driverSearch.trim().toLowerCase();
    if (!needle) return true;
    return `${driver.name} ${driver.email}`.toLowerCase().includes(needle);
  });
  const activeCallRoom = activeCall ? (
    <PharmacistDriverVideoRoom
      isOpen={!!activeCall}
      callData={activeCall}
      callState={callState}
      onCallerReady={notifyDriverWhenCallerReady}
      onClose={closeCall}
    />
  ) : null;
  const globalActiveCallRoom = activeCallRoom && typeof document !== 'undefined'
    ? createPortal(activeCallRoom, document.body)
    : activeCallRoom;

  if (!canShowCallOption) return null;

  if (triggerPlacement === 'chat-tab') {
    return (
      <>
        <div className="space-y-2">
          {loadingDrivers ? (
            <div className="support-chat-loading">Loading online assistants...</div>
          ) : drivers.length === 0 ? (
            <div className="support-chat-muted-box">No assistants are currently online.</div>
          ) : (
            <div className="support-chat-thread-list">
              {drivers.map((driver) => {
                const isStartingThisAssistant = starting && String(selectedDriverId) === String(driver.id);

                return (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() => startCall(driver.id)}
                    disabled={!!activeCall || starting}
                    className={`support-chat-thread ${isStartingThisAssistant ? 'is-active' : ''} disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <div className="support-chat-thread-main">
                      <DriverAvatar driver={driver} />
                      <div className="support-chat-thread-copy">
                        <div className="support-chat-thread-row">
                          <span className="support-chat-thread-title">{driver.name}</span>
                        </div>
                        <div className="support-chat-thread-meta-line">
                          <span className="support-chat-thread-preview">{driver.email || 'Assistant available for video call'}</span>
                        </div>
                      </div>
                    </div>
                    <span className="support-chat-status call">
                      {isStartingThisAssistant ? 'Calling' : 'Call'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {notice && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{notice}</p>}
        </div>

        {globalActiveCallRoom}
      </>
    );
  }

  const triggerClassName = triggerPlacement === 'header'
    ? 'h-12 bg-[#4E64C2] rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60'
    : triggerPlacement === 'chat'
      ? 'w-full rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#2f55d4] transition hover:border-blue-200 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 inline-flex items-center justify-center gap-2'
      : 'fixed bottom-[5.75rem] right-7 z-[185] inline-flex items-center gap-2 rounded-full border border-white/40 bg-[#2f55d4] px-4 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(47,85,212,0.35)] transition hover:bg-[#2447ba] disabled:cursor-not-allowed disabled:opacity-60';

  const callDriverButton = (
    <button
      type="button"
      onClick={openPicker}
      disabled={!!activeCall}
      className={triggerClassName}
    >
      <Video className="h-4 w-4" />
      Call Assistant
    </button>
  );

  return (
    <>
      {triggerPlacement === 'header' && headerTriggerContainer
        ? createPortal(callDriverButton, headerTriggerContainer)
        : triggerPlacement === 'chat'
          ? <div className="m-3 mb-1">{callDriverButton}</div>
        : triggerPlacement === 'floating'
          ? callDriverButton
          : null}

      {isPickerOpen && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
          <div className="relative flex max-h-[calc(100vh-3rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.18)]">
            <div className="shrink-0 border-b border-blue-100 bg-[linear-gradient(135deg,#eff6ff_0%,#f8fcff_100%)] px-5 py-4">
              <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/90 text-[#2f55d4] shadow-sm ring-1 ring-blue-100">
                  <Video className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Call Assistant</h2>
                  <p className="mt-0.5 text-sm text-slate-600">Select an assistant and start a video call.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPickerOpen(false)}
                className="rounded-full border border-slate-200 bg-white/90 p-1.5 text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                aria-label="Close assistant call picker"
              >
                <X className="h-4 w-4" />
              </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pb-5 pt-2">
              <div className="flex items-center gap-2.5 rounded-lg border border-blue-100 bg-white/95 px-3 py-2.5 shadow-sm">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={driverSearch}
                  onChange={(event) => setDriverSearch(event.target.value)}
                  placeholder="Search assistants"
                  className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>

              <div className="max-h-72 space-y-2 overflow-auto pr-1">
                {loadingDrivers ? (
                  <div className="flex items-center justify-center gap-2 rounded-xl border border-blue-100 bg-white/80 px-4 py-6 text-sm font-medium text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading assistants...
                  </div>
                ) : filteredDrivers.length === 0 ? (
                  <div className="support-chat-muted-box">{driverSearch.trim() ? 'No assistants match your search.' : 'No assistants are currently online.'}</div>
                ) : (
                  filteredDrivers.map((driver) => {
                    const isSelected = String(selectedDriverId) === String(driver.id);
                    return (
                      <button
                        key={driver.id}
                        type="button"
                        onClick={() => setSelectedDriverId(driver.id)}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left shadow-sm transition ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-950 ring-2 ring-blue-100'
                            : 'border-slate-200 bg-white/90 text-slate-900 hover:border-blue-200 hover:bg-white'
                        }`}
                      >
                        <DriverAvatar driver={driver} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{driver.name}</span>
                          {driver.email && <span className="block truncate text-xs text-slate-500">{driver.email}</span>}
                        </span>
                        <span className={`h-3.5 w-3.5 rounded-full border ${isSelected ? 'border-blue-600 bg-blue-600 ring-4 ring-blue-100' : 'border-slate-300 bg-white'}`} />
                      </button>
                    );
                  })
                )}
              </div>

              {notice && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{notice}</p>}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsPickerOpen(false)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => startCall()}
                  disabled={!selectedDriverId || starting}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#2f55d4] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2447ba] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                >
                  {starting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calling...
                    </>
                  ) : (
                    <>
                      <Video className="h-4 w-4" />
                      Start Call
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {globalActiveCallRoom}
    </>
  );
};

export default PharmacistDriverVideoCallManager;
